import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { Tooltip } from "@mui/material";
import ChannelCession from "assets/img/svg/ChannelCession";
import ChannelCollective from "assets/img/svg/ChannelCollective";
import ChannelNumerique from "assets/img/svg/ChannelNumerique";
import ChannelOther from "assets/img/svg/ChannelOther";
import ChannelPhysique from "assets/img/svg/ChannelPhysique";
import { CollectiveManagementKind } from "containers/Channels";
import classNames from "classnames";
import ModalBox from "components/Modal";
import AddSalesModal from "components/shared/AddSalesModal";
import EditSaleNoteModal from "components/shared/EditSaleNoteModal";
import DeleteModal from "components/shared/DeleteModal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import SmallSearchBar from "components/shared/SmallSearchBar";
import StyledButton from "components/shared/StyledButton";
import useAPIPagination from "hooks/useAPIPagination";
import { useWorkTitles } from "hooks/useWorkTitles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAbility } from "hooks/useAbility";
import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import type { ISalesByChannels } from "store/managementStore";
import { fetchTitleChannels } from "store/channelStore";
import { fetchDeleteSales } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { ChannelCalculationMethod, ChannelKind } from "store/types";

import AmazonIcon from "../../../../assets/img/amazon_icon.png";
import GooglePlayIcon from "../../../../assets/img/google_play_icon.png";
import IBookIcon from "../../../../assets/img/ibooks_icon.png";
import { formatLocaleDateString, formatNumber, getCountryName } from "../../../../helpers/utils";
import { salesListFilterFromSelection } from "./salesListFilter";
import SalesTitleSwitcher, { type SalesTitleSelection } from "./SalesTitleSwitcher";
import styles from "./style.module.scss";

type TProps = {
    titleName: string;
    termId: number;
    workId?: number | null;
    workAvatarUrl?: string | null;
};

const SalesDetails = ({ termId, titleName, workId, workAvatarUrl }: TProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { salesByChannel, salesByChannelsStatus } = useAppSelector((state) => state.management);
    const { titleChannels: termChannels } = useAppSelector((state) => state.channels);
    const { activePeriod } = useAppSelector((state) => state.period);
    const { namedCustomTurnoverColumns, resolveLabel } = useCalculationBaseColumnLabels();
    const turnoverLabel = resolveLabel("turnover");
    const quantityLabel = resolveLabel("quantity");
    const [salesToDelete, setSalesToDelete] = useState<number | null>(null);
    const [saleTitleIdToDelete, setSaleTitleIdToDelete] = useState<number | undefined>();
    const [saleChannelToDelete, setSaleChannelToDelete] = useState<number | null>(null);
    const [saleToEdit, setSaleToEdit] = useState<ISalesByChannels | null>(null);
    const [salesData, setSalesData] = useState(salesByChannel);
    const [isSalesModalOpen, setIsSalesModalOpen] = useState(false);
    const [selectedTitleId, setSelectedTitleId] = useState<SalesTitleSelection>("all");
    const { titles: workTitles, isLoading: workTitlesLoading } = useWorkTitles(workId);
    const ability = useAbility();
    const canUpdateSale = ability.can("update", "Sales");
    const hasForfaitChannel = termChannels.some(
        (channel) => channel.calculationMethod === ChannelCalculationMethod.PACKAGE,
    );
    const { filterTitleId, filterSalesScope } = salesListFilterFromSelection(selectedTitleId);

    const {
        query,
        handleQuery,
        count,
        page,
        setPage,
        setOffsetPagination,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
    } = useAPIPagination({
        type: "salesByChannel",
        itemId: termId,
        filterTitleId,
        filterSalesScope,
    });

    useEffect(() => {
        dispatch(fetchTitleChannels(termId));
    }, [dispatch, termId]);

    const handleSelectTitle = (nextTitleId: SalesTitleSelection) => {
        setSelectedTitleId(nextTitleId);
        setPage(0);
        setOffsetPagination(0);
    };

    const handleDeleteSale = (saleId: number, channelId: number, saleTitleId?: number) => {
        setSalesToDelete(saleId);
        setSaleChannelToDelete(channelId);
        setSaleTitleIdToDelete(saleTitleId);
    };

    useEffect(() => {
        setSalesData(salesByChannel);
    }, [salesByChannel]);

    const APIData = {
        count,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
    } as unknown as LastElementTableAPIData;

    const formatSourceColumn = (source: string, sale: any) => {
        const hasResellerSource = Boolean(source && source !== "-");

        return (
            <div className={styles["source-column"]}>
                <div style={{ display: "inline-flex" }}>
                    {source?.toLocaleLowerCase().includes("amazon") ? (
                        <img src={AmazonIcon} alt="amazon" className={styles["source-column-logo"]} />
                    ) : source?.toLocaleLowerCase().includes("google") ? (
                        <img src={GooglePlayIcon} alt="google" className={styles["source-column-logo"]} />
                    ) : source?.toLocaleLowerCase().includes("apple") ? (
                        <img src={IBookIcon} alt="google" className={styles["source-column-logo"]} />
                    ) : sale.kind === ChannelKind.PHYSICAL ? (
                        <ChannelPhysique width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.DIGITAL ? (
                        <ChannelNumerique width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.RIGHTS_TRANSFER ? (
                        <ChannelCession width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.COLLECTIVE_MANAGEMENT ? (
                        <ChannelCollective width="15px" height="15px" />
                    ) : (
                        sale.kind === ChannelKind.OTHER && <ChannelOther width="15px" height="15px" />
                    )}
                </div>
                <Tooltip title={sale.name + (hasResellerSource ? `: ${source}` : "")} arrow>
                    <div className={styles["source-column-name"]}>
                        <span className={styles["name-channel"]}>{sale.name}</span>
                        {hasResellerSource ? <span className={styles["name-source"]}>{source}</span> : null}
                    </div>
                </Tooltip>
            </div>
        );
    };

    const handleShowMore = (sale: any) => {
        const newData = salesData.map((item) => {
            if (sale.id === item.id) {
                return { ...item, showMore: !sale.showMore };
            } else {
                return item;
            }
        });

        setSalesData(newData);
    };

    const formatCollapse = (data: ISalesByChannels) => {
        const hasSpecificPrice = !!data?.overridenPrice;

        return (
            <div className={styles["collapsable-component"]}>
                <div className={styles["collapsable-details"]}>
                    <div className={styles["details-info"]}>
                        <span className={styles["info-label"]}>{t("Management.SaleDetails.Note")}</span>
                        <span>{data.note === "" ? "-" : data.note}</span>
                    </div>
                    <div className={styles["details-info"]}>
                        <span className={styles["info-label"]}>{t("Management.SaleDetails.Channel")}</span>
                        <span>{data.name}</span>
                    </div>
                    <div className={styles["details-info"]}>
                        <span className={styles["info-label"]}>{t("Management.SaleDetails.Source")}</span>
                        <span>{data.source ?? "-"}</span>
                    </div>
                    {data.kind === ChannelKind.COLLECTIVE_MANAGEMENT && (
                        <div className={styles["details-info"]}>
                            <span className={styles["info-label"]}>
                                {t("Management.SaleDetails.CollectiveManagementSociety")}
                            </span>
                            <span>
                                {data.collectiveManagementKind === CollectiveManagementKind.OTHER
                                    ? t("AddSales.CollectiveManagementKindOther")
                                    : data.collectiveManagementKind ?? "-"}
                            </span>
                        </div>
                    )}
                    {hasSpecificPrice && (
                        <div className={styles["details-info"]}>
                            <span className={styles["info-label"]}>{t("Management.SaleDetails.SpecificPrice")}</span>
                            <span>
                                {formatNumber((data?.overridenPrice ?? 0) / 100, {
                                    isCurrency: true,
                                    preferredCurrency: data?.overridenPriceCurrency,
                                })}
                            </span>
                        </div>
                    )}
                </div>
                <div className={styles["collapsable-buttons"]}>
                    {canUpdateSale && (
                        <StyledButton
                            buttonType="outlined"
                            className={styles["edit-button"]}
                            onClick={() => setSaleToEdit(data)}
                        >
                            <ModeEditOutlinedIcon sx={{ width: "15px" }} />
                            <span>{t("Management.SaleDetails.EditSale")}</span>
                        </StyledButton>
                    )}
                    <StyledButton
                        buttonType="outlined"
                        className={styles["delete-button"]}
                        onClick={() => handleDeleteSale(data.id, data.channelId, data.titleId)}
                        tooltipText={t("Management.SaleDetails.DeleteSale")}
                    >
                        <DeleteOutlineOutlinedIcon sx={{ width: "15px" }} />
                    </StyledButton>
                </div>
            </div>
        );
    };

    /**
     * A sale line names the columns it fills: when it carries no turnover of its own, the revenue is
     * the one the API computed from its copies and the applicable price.
     */
    const formatCAAmount = (sale: any) => {
        const importedTurnover = !!sale.turnover;
        const computedAmount = !importedTurnover;
        // Both columns filled: the file gave the revenue next to the copies.
        const turnoverAlongsideCopies = importedTurnover && !!sale.quantity;

        return (
            <Tooltip title={computedAmount ? t("Management.SaleDetails.ComputedCATooltip") : turnoverLabel} arrow>
                <span
                    className={classNames({
                        [styles["amount-column"]]: true,
                        [styles["computed-amount"]]: computedAmount,
                        [styles["turnover"]]: turnoverAlongsideCopies,
                    })}
                >
                    {importedTurnover
                        ? formatNumber((sale.turnover ?? 0) / 100, {
                              isCurrency: true,
                          })
                        : formatNumber(sale.computedRevenue / 100, {
                              isCurrency: true,
                              preferredCurrency: sale.overridenPriceCurrency,
                          })}
                </span>
            </Tooltip>
        );
    };

    const salesColumns: LastElementTableColumns[] = [
        {
            id: "source",
            label: t("Management.Channel"),
            format: (value, id, currentRow) => formatSourceColumn(value, currentRow),
            align: "left",
            sort: true,
            maxWidth: "150px",
        },
        {
            id: "turnover",
            label: turnoverLabel,
            align: "right",
            format: (value, id, currentRow) => formatCAAmount(currentRow),
            sort: true,
        },
        {
            id: "quantity",
            label: quantityLabel,
            align: "right",
            sort: true,
            format: (value) => value || "-",
        },
        ...(filterSalesScope === "term"
            ? []
            : namedCustomTurnoverColumns.map(({ column, label }) => ({
                  id: column,
                  label,
                  align: "right" as const,
                  sort: true,
                  format: (value: number | null) => (value ? formatNumber(value / 100, { isCurrency: true }) : "-"),
              }))),
        {
            id: "returnedSales",
            label: t("Management.SalesReturns"),
            align: "right",
            sort: true,
            maxWidth: "80px",
            minWidth: "80px",
            // Returns are counted in copies: a line without quantity has nothing to show.
            format: (value, id, currentRow) => (currentRow.quantity ? value : "-"),
        },
        {
            id: "created",
            label: t("Management.DateAdded"),
            align: "right",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, showTime: true } }),

            sort: true,
        },
        {
            id: "countryMarket",
            label: t("Management.CountrySale"),
            align: "right",
            format: (value) => getCountryName(value) ?? "-",
            hidden: salesData.every((sale) => !sale.countryMarket),
        },
        {
            id: "date",
            label: t("Management.SaleDate"),
            align: "right",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
            sort: true,
        },
        {
            id: "showMore",
            label: "",
            align: "right",
            format: (value, id, currentRow) =>
                currentRow.showMore ? (
                    <ExpandLessIcon className={styles["expand-icon"]} onClick={() => handleShowMore(currentRow)} />
                ) : (
                    <ExpandMoreIcon className={styles["expand-icon"]} onClick={() => handleShowMore(currentRow)} />
                ),
        },
    ];

    const tBodyStyles = {
        padding: "10px",
        fontSize: "0.75rem",
    };

    const handleDeleteSales = async () => {
        if (activePeriod && saleChannelToDelete && salesToDelete) {
            await dispatch(
                fetchDeleteSales({
                    ...(saleTitleIdToDelete != null ? { titleId: saleTitleIdToDelete } : {}),
                    salesId: salesToDelete,
                    periodId: activePeriod,
                    channelId: saleChannelToDelete,
                    termId,
                }),
            );
            setSalesToDelete(null);
        }
    };

    return (
        <div className={styles["sales-details-wrapper"]}>
            {saleToEdit && (
                <ModalBox onClose={() => setSaleToEdit(null)} open mainTitle={t("EditSaleNote.Title")}>
                    <EditSaleNoteModal sale={saleToEdit} onClose={() => setSaleToEdit(null)} />
                </ModalBox>
            )}
            <ModalBox
                onClose={() => setIsSalesModalOpen(false)}
                open={isSalesModalOpen}
                mainTitle={t("AddSales.Title")}
                helpdeskInfo={{
                    text: t("AddSales.SalesInfo"),
                    link: "https://helpdesk.crealo.app/fr/article/ajouter-des-ventes-10nni0m/",
                }}
            >
                <AddSalesModal
                    termId={termId}
                    onClose={() => setIsSalesModalOpen(false)}
                    from="details"
                    onSalesCreated={handleRequest}
                />
            </ModalBox>
            <DeleteModal
                open={!!salesToDelete}
                onClose={() => setSalesToDelete(null)}
                elementTitle={t("DeleteModal.SaleTitle", { titleName: titleName })}
                onDelete={handleDeleteSales}
                description={t("DeleteSales.DeleteIsIrreversible")}
            />

            {!workTitlesLoading && (
                <SalesTitleSwitcher
                    titles={workTitles}
                    selectedTitleId={selectedTitleId}
                    onSelect={handleSelectTitle}
                    showForfait={hasForfaitChannel}
                    workName={titleName}
                    workAvatarUrl={workAvatarUrl}
                />
            )}
            <SmallSearchBar filter={query} onSearch={handleQuery} placeholder={t("Payment.SearchSalesPlaceholder")} />

            <LastElementTable
                columns={salesColumns}
                data={salesData}
                max={-1}
                tableBodyStyles={tBodyStyles}
                collapseOn={(_) => "showMore"}
                collapseContent={(row) => formatCollapse(row)}
                collapseSelectedBackground="#ffff"
                dataType="sales"
                emptyStateAction={() => setIsSalesModalOpen(true)}
                maxHeight={700}
                dataCount={count}
                APIData={APIData}
                isLoading={salesByChannelsStatus === "pending"}
                columnsNumber={salesColumns.filter((column) => !column.hidden).length}
            />
        </div>
    );
};

export default SalesDetails;
